import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const TOKEN_KEY = 'da_token';

/**
 * Singleton-ish socket connection. Authenticates with the user's JWT.
 * Returns: { socket, connected, lobby, joinedRoom, countdown }
 * and helper actions: createRoom, joinRoom, leaveRoom, sendMove, endGame, sendChat, refreshLobby.
 *
 * Pass `gameFilter='tictactoe'` (or 'domino') to filter the lobby per game.
 */
export default function useDominoSocket(enabled = true, gameFilter = 'domino') {
  const sockRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lobby, setLobby] = useState([]);
  const [joinedRoom, setJoinedRoom] = useState(null); // { room_id, type, bet, players, status }
  const [countdown, setCountdown] = useState(null);
  const [matchStarted, setMatchStarted] = useState(null); // { first_turn, bet, players }
  const [incomingMove, setIncomingMove] = useState(null); // { move, by, next_turn, _id }
  const [matchEnded, setMatchEnded] = useState(null);
  const [gameInit, setGameInit] = useState(null);         // { game:'domino', hand, boneyard_count, first_turn, opponent, bet }
  const [handsUpdate, setHandsUpdate] = useState(null);   // { boneyard_count, players:[{user_id,count}] }
  const [opponentNetworkStatus, setOpponentNetworkStatus] = useState(null); // 'disconnected' | 'reconnected' | null
  const [resync, setResync] = useState(null);             // { moves, turn_idx } for TTT-style replay
  const [chat, setChat] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const url = process.env.REACT_APP_BACKEND_URL;
    const s = io(url, {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });
    sockRef.current = s;

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', () => setConnected(false));

    s.on('lobby:update', (payload) => {
      const all = payload?.rooms || [];
      setLobby(all.filter((r) => (r.game || 'domino') === gameFilter));
    });
    s.on('balance:update', (payload) => {
      try { window.dispatchEvent(new CustomEvent('da:balance-update', { detail: payload })); } catch (e) { /* ignore */ }
    });
    s.on('room:countdown', ({ seconds }) => setCountdown(seconds));
    s.on('room:start', (payload) => { setCountdown(null); setMatchStarted(payload); });
    s.on('game:init', (payload) => { setGameInit({ ...payload, _id: Date.now() + Math.random() }); });
    s.on('domino:hands_update', (payload) => setHandsUpdate({ ...payload, _id: Date.now() + Math.random() }));
    s.on('room:player_disconnected', (p) => setOpponentNetworkStatus({ kind: 'disconnected', ...p, _id: Date.now() }));
    s.on('room:player_reconnected', (p) => setOpponentNetworkStatus({ kind: 'reconnected', ...p, _id: Date.now() }));
    s.on('room:resync', (payload) => setResync({ ...payload, _id: Date.now() + Math.random() }));
    s.on('room:player_joined', () => { /* could refresh joinedRoom */ });
    s.on('room:player_left', () => { /* could mark match aborted */ });
    s.on('room:expired', () => { setJoinedRoom(null); setCountdown(null); setMatchStarted(null); });
    s.on('game:move', (m) => setIncomingMove({ ...m, _id: Date.now() + Math.random() }));
    s.on('game:end', (e) => setMatchEnded(e));
    s.on('chat:message', (m) => setChat((c) => [...c.slice(-19), { ...m, ts: Date.now() }]));

    return () => {
      try { s.disconnect(); } catch (_) { /* ignore */ }
      sockRef.current = null;
    };
  }, [enabled, gameFilter]);

  const createRoom = useCallback((opts) => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false, error: 'not_connected' });
    s.emit('room_create', { ...opts, game: opts?.game || gameFilter }, (res) => {
      if (res?.ok) setJoinedRoom({ ...res.room, type: opts.type || 'public' });
      resolve(res || { ok: false });
    });
  }), [gameFilter]);

  const joinRoom = useCallback((opts) => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false, error: 'not_connected' });
    s.emit('room_join', opts, (res) => {
      if (res?.ok) setJoinedRoom(res.room);
      resolve(res || { ok: false });
    });
  }), []);

  const leaveRoom = useCallback(() => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false });
    s.emit('room_leave', {}, (res) => {
      setJoinedRoom(null); setCountdown(null); setMatchStarted(null); setMatchEnded(null);
      resolve(res || { ok: true });
    });
  }), []);

  const sendMove = useCallback((data) => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false });
    s.emit('game_move', data, (res) => resolve(res || { ok: false }));
  }), []);

  const endGame = useCallback((data) => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false });
    s.emit('game_end', data, (res) => resolve(res || { ok: false }));
  }), []);

  const sendChat = useCallback((text, avatar) => {
    const s = sockRef.current; if (!s) return;
    s.emit('chat_message', { text, avatar });
  }, []);

  const refreshLobby = useCallback(() => {
    const s = sockRef.current; if (!s) return;
    s.emit('lobby_list', { game: gameFilter });
  }, [gameFilter]);

  const clearIncomingMove = useCallback(() => setIncomingMove(null), []);
  const clearMatchStarted = useCallback(() => setMatchStarted(null), []);
  const clearGameInit = useCallback(() => setGameInit(null), []);
  const clearHandsUpdate = useCallback(() => setHandsUpdate(null), []);
  const dominoDraw = useCallback(() => new Promise((resolve) => {
    const s = sockRef.current; if (!s) return resolve({ ok: false });
    s.emit('domino_draw', {}, (res) => resolve(res || { ok: false }));
  }), []);

  return {
    connected,
    lobby,
    joinedRoom,
    countdown,
    matchStarted,
    incomingMove,
    matchEnded,
    gameInit,
    handsUpdate,
    opponentNetworkStatus,
    resync,
    chat,
    createRoom, joinRoom, leaveRoom, sendMove, endGame, sendChat, refreshLobby, dominoDraw,
    clearIncomingMove, clearMatchStarted, clearGameInit, clearHandsUpdate,
    clearOpponentNetwork: () => setOpponentNetworkStatus(null),
    clearResync: () => setResync(null),
  };
}
